import React, { useState, useEffect, useRef } from 'react';
import Switch from 'react-switch';
import {
  Mail,
  MessageSquare,
  MessageCircle,
  FileText,
  File,
  Printer,
} from 'lucide-react';

const iconMap = {
  mail: Mail,
  'message-square': MessageSquare,
  'message-circle': MessageCircle,
  'file-text': FileText,
  file: File,
  printer: Printer,
};

const Sections = ({ data, editData, setEditedData }) => {
  const [sectionsState, setSectionsState] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isInitialMount = useRef(true);
  const prevEditData = useRef(null);

  // Only initialize once when data and editData are available
  useEffect(() => {
    // Skip if we don't have data or editData
    if (!data?.sections || !editData?.preferredConfig) {
      setIsLoading(true);
      return;
    }

    // Skip if editData hasn't changed and we've already initialized
    if (
      prevEditData.current === editData &&
      !isInitialMount.current &&
      sectionsState.length > 0
    ) {
      return;
    }

    // Save reference to current editData
    prevEditData.current = editData;

    // Create sections state from data structure and editData values
    const updatedSections = data.sections.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        const savedPreference = editData.preferredConfig[item.id] || { enabled: false, copies: 0 };
        return {
          ...item,
          enabled: savedPreference.enabled,
          defaultValue: savedPreference.copies,
        };
      }),
    }));

    setSectionsState(updatedSections);
    setIsLoading(false);
    isInitialMount.current = false;
  }, [data, editData]);

  // Only update editedData when user makes changes
  const updateEditedData = (newSectionsState) => {
    const preferredConfig = {};
    newSectionsState.forEach((section) => {
      section.items.forEach((item) => {
        preferredConfig[item.id] = {
          enabled: item.enabled,
          copies: item.enabled ? item.defaultValue : 0,
        };
      });
    });

    setEditedData((prev) => ({
      ...prev,
      preferredConfig,
    }));
  };

  const updateItem = (sectionIndex, itemIndex, updater) => {
    setSectionsState((prevState) => {
      const newState = [...prevState];
      const item = { ...newState[sectionIndex].items[itemIndex] };
      Object.assign(item, updater(item));
      newState[sectionIndex].items[itemIndex] = item;
      
      // Only update editedData when user makes a change
      updateEditedData(newState);
      
      return newState;
    });
  };

  const toggleItem = (sectionIndex, itemIndex) => {
    updateItem(sectionIndex, itemIndex, (item) => ({
      enabled: !item.enabled,
    }));
  };

  const changeCounter = (sectionIndex, itemIndex, increment) => {
    updateItem(sectionIndex, itemIndex, (item) => {
      const value = item.defaultValue || 0;
      const newValue = increment
        ? Math.min(value + 1, item.maxValue || 100)
        : Math.max(value - 1, item.minValue || 0);
      return { defaultValue: newValue };
    });
  };

  const renderIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Mail;
    return <IconComponent size={20} />;
  };

  const renderControl = (item, sectionIndex, itemIndex) => {
    if (item.type === 'toggle') {
      return (
        <Switch
          checked={item.enabled}
          onChange={() => toggleItem(sectionIndex, itemIndex)}
          onColor="#3b82f6"
          offColor="#d2d5da"
          handleDiameter={20}
          uncheckedIcon={false}
          checkedIcon={false}
          height={24}
          width={40}
        />
      );
    }

    if (item.type === 'counter') {
      return (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">{item.counterLabel}:</span>
          <div className="flex items-center border rounded overflow-hidden">
            <button
              onClick={() => changeCounter(sectionIndex, itemIndex, false)}
              className="px-2 py-1 bg-gray-100 hover:bg-gray-200"
              disabled={item.defaultValue <= (item.minValue || 0)}
            >
              -
            </button>
            <input
              type="number"
              value={item.defaultValue}
              readOnly
              className="w-9 text-center border-0 focus:ring-0 focus:outline-none"
            />
            <button
              onClick={() => changeCounter(sectionIndex, itemIndex, true)}
              className="px-2 py-1 bg-gray-100 hover:bg-gray-200"
              disabled={item.defaultValue >= (item.maxValue || 100)}
            >
              +
            </button>
          </div>
          <Switch
            checked={item.enabled}
            onChange={() => toggleItem(sectionIndex, itemIndex)}
            onColor="#3b82f6"
            offColor="#d2d5da"
            handleDiameter={20}
            uncheckedIcon={false}
            checkedIcon={false}
            height={24}
            width={40}
          />
        </div>
      );
    }

    return null;
  };

  if (isLoading) {
    return <div>Loading preferences...</div>;
  }

  return (
    <div className="space-y-8">
      {sectionsState.map((section, sectionIndex) => (
        <div key={section.id} className="space-y-4">
          <div className="text-lg font-semibold">{section.title}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.items.map((item, itemIndex) => (
              <div
                key={item.id}
                className="p-6 py-3 rounded-lg w-full flex items-center justify-between border-2 border-f3f8ff"
              >
                <div className="flex items-center space-x-3">
                  {renderIcon(item.icon)}
                  <p className="text-lg">{item.label}</p>
                </div>
                {renderControl(item, sectionIndex, itemIndex)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Sections;